interface SparklineProps {
  data: number[];
  w?: number;
  h?: number;
  /** Stroke tinta unita (default: currentColor, cioè --accent). */
  color?: string;
  /** Riempimento area sotto la curva (tinta piena a bassa opacità). Default true. */
  showArea?: boolean;
  /** Pallino sull'ultimo punto. Default true. */
  lastDot?: boolean;
  /** La linea si "disegna" da sinistra a destra all'ingresso (one-shot). */
  animate?: boolean;
}

/** Catmull-Rom → Bézier: curva morbida che passa per tutti i punti. */
function smoothPath(pts: Array<[number, number]>): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0]![0]},${pts[0]![1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return d;
}

/**
 * Micro-grafico a area: curva morbida con riempimento piatto + endpoint dot.
 * Sostituisce la polyline "Excel 2007". Niente <defs>: l'area è currentColor
 * a bassa opacità, così non servono id per istanza.
 */
export function Sparkline({ data, w = 80, h = 22, color, showArea = true, lastDot = true, animate = false }: SparklineProps) {
  if (data.length < 2) return null;

  const pad = 2.5; // margine verticale per non clippare stroke/dot
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const step = w / (data.length - 1);
  const pts: Array<[number, number]> = data.map((v, i) => [
    i * step,
    pad + (h - pad * 2) - ((v - min) / span) * (h - pad * 2),
  ]);

  // Monocroma: eredita currentColor (= tone-indice della card). Nessun arcobaleno.
  const stroke = color ?? 'currentColor';
  const line = smoothPath(pts);
  const last = pts[pts.length - 1]!;
  const area = `${line} L ${last[0].toFixed(2)},${h} L 0,${h} Z`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible', color: color ?? 'var(--accent)' }} aria-hidden="true">
      {showArea && <path d={area} fill="currentColor" fillOpacity="0.08" stroke="none" className={animate ? 'spk-area-anim' : undefined} />}
      <path
        d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        {...(animate ? { pathLength: 1, className: 'spk-line-anim' } : {})}
      />
      {lastDot && <circle cx={last[0]} cy={last[1]} r="2.2" fill={color ?? 'currentColor'} stroke="var(--surface)" strokeWidth="1.5" className={animate ? 'spk-dot-anim' : undefined} />}
    </svg>
  );
}
